// ============================================
// SammyGas - Contexto de Autenticación
// ============================================

import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import authService from '../services/authService';
import { STORAGE_KEYS } from '../utils/constants';

// Crear el contexto
export const AuthContext = createContext(null);

/**
 * Provider de autenticación que envuelve la aplicación
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
export const AuthProvider = ({ children }) => {
  // Estados
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);

  // ============================================
  // INICIALIZACIÓN - Verificar token al cargar
  // ============================================
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
        const storedUser = localStorage.getItem(STORAGE_KEYS.USER);

        if (token) {
          // Verificar que el token sea válido obteniendo el perfil
          try {
            const userData = await authService.getProfile();
            setUser(userData);
            setIsAuthenticated(true);
          } catch (error) {
            // Token inválido o expirado
            console.warn('Token inválido o expirado');
            localStorage.removeItem(STORAGE_KEYS.TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER);
            setUser(null);
            setIsAuthenticated(false);
          }
        } else if (storedUser) {
          // No hay token pero hay usuario guardado (inconsistencia)
          localStorage.removeItem(STORAGE_KEYS.USER);
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error al inicializar autenticación:', error);
      } finally {
        setIsLoading(false);
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, []);

  // ============================================
  // ESCUCHAR EVENTO DE LOGOUT GLOBAL
  // ============================================
  useEffect(() => {
    const handleLogoutEvent = () => {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    };

    window.addEventListener('auth:logout', handleLogoutEvent);

    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent);
    };
  }, []);

  // ============================================
  // LOGIN
  // ============================================
  const login = useCallback(async (credentials) => {
    setIsLoading(true);
    try {
      const response = await authService.login(credentials);
      
      setUser(response.user);
      setIsAuthenticated(true);
      
      return { success: true, user: response.user };
    } catch (error) {
      console.error('Error en login:', error);
      return { 
        success: false, 
        error: error.message || 'Error al iniciar sesión',
        type: error.type || 'UNKNOWN_ERROR',
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================
  // REGISTER
  // ============================================
  const register = useCallback(async (data) => {
    setIsLoading(true);
    try {
      const response = await authService.register(data);
      
      setUser(response.user);
      setIsAuthenticated(true);
      
      return { success: true, user: response.user };
    } catch (error) {
      console.error('Error en registro:', error);
      return { 
        success: false, 
        error: error.message || 'Error al registrarse',
        type: error.type || 'UNKNOWN_ERROR',
        errors: error.errors || null,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================
  // LOGOUT
  // ============================================
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      // Intentar logout en el servidor (ignorar errores)
      try {
        await authService.logout();
      } catch (serverError) {
        console.warn('Error al hacer logout en servidor:', serverError);
      }
      
      // Limpiar estado local
      setUser(null);
      setIsAuthenticated(false);
      
      return { success: true };
    } catch (error) {
      console.error('Error en logout:', error);
      return { 
        success: false, 
        error: error.message || 'Error al cerrar sesión',
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================
  // UPDATE PROFILE
  // ============================================
  const updateProfile = useCallback(async (data) => {
    setIsLoading(true);
    try {
      const updatedUser = await authService.updateProfile(data);
      
      setUser(updatedUser);
      
      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      return { 
        success: false, 
        error: error.message || 'Error al actualizar perfil',
        type: error.type || 'UNKNOWN_ERROR',
        errors: error.errors || null,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================
  // CHANGE PASSWORD
  // ============================================
  const changePassword = useCallback(async (data) => {
    setIsLoading(true);
    try {
      const result = await authService.changePassword(data);
      
      return { success: true, message: result.message };
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      return { 
        success: false, 
        error: error.message || 'Error al cambiar contraseña',
        type: error.type || 'UNKNOWN_ERROR',
        errors: error.errors || null,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================
  // REFRESH USER (actualizar datos del usuario)
  // ============================================
  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Error al refrescar usuario:', error);
      return null;
    }
  }, []);

  // ============================================
  // VALOR DEL CONTEXTO
  // ============================================
  const value = {
    // Estados
    user,
    isAuthenticated,
    isLoading,
    isInitializing,
    
    // Funciones
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export default AuthContext;
